// ============================================================
// Hirafi — Page /q/:token
//
// Consultation publique d'un devis sans authentification.
// Le token est passé dans l'URL — aucune auth requise.
// ============================================================

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { DevisPreviewPrint } from "@/features/devis/components/DevisPreviewPrint";
import type { StandaloneDevisFormData } from "@/features/devis/types";

interface PublicQuoteData {
  id: string;
  title: string;
  description: string;
  total_amount: number;
  currency: string;
  valid_until: string;
  estimated_intervention_delay?: string;
  notes?: string;
  artisan_name?: string;
  artisan_email?: string;
  artisan_phone?: string;
  artisan_address?: string;
  artisan_activity?: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  public_token: string;
  line_items?: Array<{
    label: string;
    description?: string;
    quantity: number;
    unit: string;
    unit_price: number;
    total_price: number;
    display_order: number;
  }>;
}

/** Convertit les données DB en StandaloneDevisFormData pour le template print */
function toFormData(q: PublicQuoteData): StandaloneDevisFormData {
  return {
    artisan: {
      name: q.artisan_name ?? "",
      email: q.artisan_email ?? "",
      phone: q.artisan_phone ?? "",
      address: q.artisan_address ?? "",
      activity: q.artisan_activity ?? "",
    },
    client: {
      name: q.client_name ?? "",
      email: q.client_email ?? "",
      phone: q.client_phone ?? "",
      address: q.client_address ?? "",
    },
    title: q.title,
    description: q.description,
    totalAmount: q.total_amount,
    currency: q.currency,
    validUntil: q.valid_until.split("T")[0],
    estimatedDelay: q.estimated_intervention_delay,
    notes: q.notes ?? undefined,
    lineItems: (q.line_items ?? []).map((li) => ({
      label: li.label,
      description: li.description,
      quantity: li.quantity,
      unit: li.unit,
      unit_price: li.unit_price,
      total_price: li.total_price,
    })),
  };
}

export default function DevisPublicPage() {
  const { token } = useParams<{ token: string }>();
  const [quote, setQuote] = useState<PublicQuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // SEC-006 : validation UUID avant tout appel RPC
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!token || !UUID_RE.test(token)) {
      setError("Lien invalide.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc(
          "get_quote_by_public_token" as any,
          { p_token: token }
        );

        if (rpcError) throw rpcError;
        if (!data) {
          setError("Ce devis n'existe pas ou a été supprimé.");
          return;
        }

        setQuote(data as PublicQuoteData);
      } catch (e: any) {
        setError(e.message || "Impossible de charger le devis.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Chargement du devis…</p>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background px-4">
        <AlertCircle className="h-10 w-10 text-destructive/60" />
        <p className="text-sm text-muted-foreground text-center">{error}</p>
        <a href="/" className="text-sm text-primary underline">
          Retour à l'accueil
        </a>
      </div>
    );
  }

  const formData = toFormData(quote);
  const shortId = `HIRAFI-${quote.id.slice(-6).toUpperCase()}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barre d'actions */}
      <div className="no-print bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm">{quote.title}</p>
            <p className="text-xs text-muted-foreground">{shortId}</p>
          </div>
          <button
            onClick={() => {
            const prev = document.title;
            document.title = `Devis — ${quote.title}`;
            document.body.classList.add("printing-devis");
            window.print();
            const cleanup = () => {
              document.title = prev;
              document.body.classList.remove("printing-devis");
              window.removeEventListener("afterprint", cleanup);
            };
            window.addEventListener("afterprint", cleanup);
          }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors"
          >
            Télécharger PDF
          </button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <DevisPreviewPrint data={formData} quoteNumber={shortId} />
      </div>
    </div>
  );
}
